/**
 * Product Data Context
 *
 * Manages server data fetching and provides product configuration data
 * including variations, attributes, and terms.
 *
 * @module contexts/product-data
 */

import apiFetch from '@wordpress/api-fetch';
import { log } from '../../../utils/logging';
import type { ConfiguratorData } from '../../../types';

/**
 * Creates reactive product data state
 *
 * Responsibilities:
 * - Fetch product data from API
 * - Handle loading states and errors
 * - Provide variations and attributes data
 * - Support retry on failure
 *
 * @param apiUrl - The API endpoint URL to fetch configurator data
 * @returns Product data context with state and methods
 */
export function createProductDataContext(apiUrl: string) {
	// State
	let configData = $state<ConfiguratorData | null>(null);
	let loading = $state(true);
	let error = $state('');

	/**
	 * Derived: Extract attribute names dynamically from first variation
	 * This ensures the configurator works with any product attributes
	 */
	const attributeNames = $derived.by(() => {
		if (!configData || configData.variations.length === 0) return [];
		return Object.keys(configData.variations[0].attributes);
	});

	/**
	 * Derived: Get all variations
	 */
	const variations = $derived.by(() => {
		return configData?.variations || [];
	});

	/**
	 * Derived: Get all attributes
	 */
	const attributes = $derived.by(() => {
		return configData?.attributes || [];
	});

	/**
	 * Derived: Check if product is a balloon product
	 */
	const isBalloonProduct = $derived.by(() => {
		return configData?.product.is_balloon_product || false;
	});

	/**
	 * Derived: Check if product is a non-print product (no printing, no digital proof)
	 */
	const isNonPrintProduct = $derived.by(() => {
		return configData?.product.is_non_print_product || false;
	});

	/**
	 * Derived: Get current language
	 */
	const currentLanguage = $derived.by(() => {
		return configData?.product.current_language || 'nl';
	});

	/**
	 * Derived: Get service info
	 */
	const serviceInfo = $derived.by(() => {
		return configData?.product.service_info;
	});

	/**
	 * Helper: Get attribute by slug
	 *
	 * @param slug - The attribute slug to find (with or without 'pa_' prefix)
	 * @returns The attribute or undefined
	 */
	function getAttributeBySlug(slug: string) {
		if (!configData) return undefined;

		// Try exact match first
		let attribute = configData.attributes.find((attr) => attr.slug === slug);

		// If not found and slug has 'pa_' prefix, try without it
		if (!attribute && slug.startsWith('pa_')) {
			const withoutPrefix = slug.slice(3);
			attribute = configData.attributes.find((attr) => attr.slug === withoutPrefix);
		}

		// If not found and slug doesn't have 'pa_' prefix, try with it
		if (!attribute && !slug.startsWith('pa_')) {
			const withPrefix = `pa_${slug}`;
			attribute = configData.attributes.find((attr) => attr.slug === withPrefix);
		}

		return attribute;
	}

	/**
	 * Helper: Get attributes by type
	 *
	 * @param type - The attribute type to filter by
	 * @returns Array of attributes matching the type
	 */
	function getAttributesByType(type: 'color' | 'size' | 'printing' | 'default') {
		return configData?.attributes.filter((attr) => attr.type === type) || [];
	}

	/**
	 * Helper: Get terms for an attribute
	 *
	 * @param attributeSlug - The attribute slug (may include 'pa_' prefix)
	 * @returns Array of terms for the attribute
	 */
	function getTermsForAttribute(attributeSlug: string) {
		if (!configData) return [];

		const attribute = getAttributeBySlug(attributeSlug);
		if (!attribute) return [];

		// Terms are keyed by taxonomy slug (with 'pa_' prefix)
		// Try with the attribute's slug as-is first
		let terms = configData.terms[attribute.slug];

		// If not found, try with 'pa_' prefix
		if (!terms && !attribute.slug.startsWith('pa_')) {
			terms = configData.terms[`pa_${attribute.slug}`];
		}

		// If not found, try without 'pa_' prefix
		if (!terms && attribute.slug.startsWith('pa_')) {
			terms = configData.terms[attribute.slug.slice(3)];
		}

		return terms || [];
	}

	/**
	 * Helper: Get attribute label (translated, human-readable name)
	 *
	 * @param attributeSlug - The attribute slug (may include 'pa_' prefix)
	 * @returns The translated label or the slug as fallback
	 */
	function getAttributeLabel(attributeSlug: string): string {
		const attribute = getAttributeBySlug(attributeSlug);
		return attribute?.label || attributeSlug;
	}

	/**
	 * Helper: Check if attribute is color type
	 *
	 * @param attributeSlug - The attribute slug (may include 'pa_' prefix)
	 * @returns True if attribute is color type
	 */
	function isColorAttribute(attributeSlug: string): boolean {
		const attribute = getAttributeBySlug(attributeSlug);
		return attribute?.type === 'color';
	}

	/**
	 * Helper: Find variation by attribute selections
	 *
	 * @param selections - Object of attribute slug => term slug
	 * @returns The matching variation or null
	 */
	function findVariationBySelections(selections: Record<string, string>) {
		if (!configData) return null;

		return (
			configData.variations.find((variation) => {
				// Check if all selections match this variation's attributes
				return Object.entries(selections).every(([attrSlug, termSlug]) => {
					return variation.attributes[attrSlug] === termSlug;
				});
			}) || null
		);
	}

	/**
	 * Helper: Get image for a specific variation by selections
	 *
	 * @param selections - Object of attribute slug => term slug
	 * @returns Image URL or null
	 */
	function getVariationImage(selections: Record<string, string>): string | null {
		const variation = findVariationBySelections(selections);
		return variation?.image || null;
	}

	/**
	 * Load configurator data from API
	 */
	async function loadData() {
		try {
			loading = true;
			error = '';

			const response = await apiFetch<ConfiguratorData>({
				url: apiUrl,
			});

			configData = response;
			log('Configurator data loaded:', 'log', response);
		} catch (err) {
			error = 'Failed to load configurator data. Please try again.';
			log('Failed to load configurator data:', 'error', err);
		} finally {
			loading = false;
		}
	}

	/**
	 * Retry loading data after an error
	 */
	async function retry() {
		await loadData();
	}

	return {
		// State
		get configData() {
			return configData;
		},
		get loading() {
			return loading;
		},
		get error() {
			return error;
		},

		// Derived (use getters to access $derived values)
		get attributeNames() {
			return attributeNames;
		},
		get attributes() {
			return attributes;
		},
		get variations() {
			return variations;
		},
		get isBalloonProduct() {
			return isBalloonProduct;
		},
		get isNonPrintProduct() {
			return isNonPrintProduct;
		},
		get currentLanguage() {
			return currentLanguage;
		},
		get serviceInfo() {
			return serviceInfo;
		},

		// Helper Methods
		getAttributeBySlug,
		getAttributesByType,
		getTermsForAttribute,
		getAttributeLabel,
		isColorAttribute,
		findVariationBySelections,
		getVariationImage,

		// Methods
		loadData,
		retry,
	};
}

/**
 * Type for product data context
 */
export type ProductDataContext = ReturnType<typeof createProductDataContext>;
